'use client'

import {
  Button,
  BaseDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IconColor,
  Spinner,
  Tooltip,
  cn,
  useToast,
  useAuth,
} from '@/dc-shared-web/src'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@dc-shared-web/components/shadcn/form'
import {
  faChevronRight,
  faDownload,
} from '@document-crunch/pro-regular-svg-icons'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useFSTrackEvent } from '@dc-shared-web/hooks/api/analytic/useFSTrackEvent'
import {
  useActivityService,
  ActivityCategory,
  ActivitySubCategory,
  ActivityItemType,
} from '@dc-shared-web/hooks/api/activity'
import { useForm } from 'react-hook-form'
import { ProjectSightModalWrapper } from '@/app/core/components/ProjectSight'
import { useEffect, useState } from 'react'
import { Instance } from '@nutrient-sdk/viewer'
import { useRollbar } from '@rollbar/react'

import type {
  ChecklistPreview,
  CrunchData,
} from '@/dc-shared-web/src/hooks/api/document/types'
import { Checkbox } from '@/dc-shared-web/src/components/shadcn/checkbox'
import { useLDFlags } from '@/app/core/hooks/useLDFlags'
import {
  COMPRESS_TEXT,
  KEY_PROVISION_MATCHES,
  LANDSCAPE,
  TABLE_OF_CONTENTS,
} from '@/app/crunches/[id]/constants'
import { OPTION_GROUPS } from '@/app/crunches/[id]/InfoPanel/constants'
import DownloadDialogExpanded from '@/app/crunches/[id]/Subheader/DownloadDialogExpanded'
import { downloadPdf } from '@/app/crunches/[id]/Subheader/download'
import { usePostDownloadPdf } from '@/dc-shared-web/src/hooks/api/document/usePostDownload'
import { useEnvContext } from 'next-runtime-env'
import { updateLinkAnnotations } from '@/app/crunches/[id]/Subheader/helpers'
import { UDR_SOLUTION_TYPE_NAME } from '@/dc-shared-web/src/hooks/api/document/constants'
import useLocalStorage from '@/dc-shared-web/src/hooks/utils/useLocalStorage'
import { FS } from '@/dc-shared-web/src/lib/trackingConstants'
import capitalize from 'lodash/capitalize'
import {
  BodyDataDownload,
  ChecklistDataDownload,
} from '@/dc-shared-web/src/hooks/api/document/postDownloadPdf'
import { DownloadDocumentPlaybooksettings } from '@/app/download/[id]/types'

const checklistOptionsSchema = z.object({
  uuid: z.string(),
  include: z.boolean(),
  include_option1: z.boolean(),
  include_option2: z.boolean(),
  include_option3: z.boolean(),
  include_gen_ai_summary: z.boolean(),
  include_notes: z.boolean(),
  include_subquestions: z.boolean(),
  include_descriptions: z.boolean(),
  include_no_answer: z.boolean(),
})

// no toc, no highlights, no cheatsheet, checklist is checklists now, no notes
const formSchema = z.object({
  document: z.boolean(),
  crunchUuid: z.string(),
  includeProject: z.boolean(),
  includeKeyProvisionMatches: z.boolean(),
  includeNotesMarkups: z.boolean(),
  includeProvisionHighlights: z.boolean(),
  useOriginalDoc: z.boolean(),
  compress_text: z.boolean(),
  landscape: z.boolean(),
  checklists: z.array(checklistOptionsSchema),
  playbooks: z.array(
    z.object({
      uuid: z.string(),
      include: z.boolean(),
    })
  ),
})

export type DownloadFormValues = z.infer<typeof formSchema>
export type ChecklistFormOptions = z.infer<typeof checklistOptionsSchema>
export type ChecklistOptions = {
  label: string
  fieldName: `checklists.${number}`
  className: string
  show: boolean
  id: string
  optionGroup: 1 | 2 | 3
  questionOptions: {
    label: string
    fieldName: string
  }[]
  additionalOptions: {
    label: string
    fieldName: string
    show?: boolean
  }[]
}
export type DocumentOptions = {
  label: string
  fieldName: 'document'
  className: string
  show: boolean
  id: string
  additionalOptions: {
    label: string
    fieldName: string
    className: string
    show: boolean
    description: string
  }[]
  layoutOptions: {
    label: string
    fieldName: string
    className: string
    show: boolean
    description: string
  }[]
}

const optionClassname = 'tw-pl-7'

export default function DownloadDialog({
  pspdfkitInstance,
  crunchData,
  defaultOpen = false,
  isVersionCompV2Selected,
}: {
  pspdfkitInstance: Instance | null
  crunchData: CrunchData
  defaultOpen?: boolean
  isVersionCompV2Selected: boolean
}) {
  const {
    flags: {
      keyProvisionText,
      downloadConfigOptions,
      versionCompareDiscoverabilityImprovements,
    },
  } = useLDFlags()
  const { user } = useAuth()
  // Find the team associated with the current crunch
  const crunchTeam = user.teams.find((team) => team.id === crunchData.teamUuid)
  const projectSightIntegration =
    crunchTeam?.entitlements?.project_sight || false
  const { NEXT_PUBLIC_PSPDF_LICENCE_KEY } = useEnvContext()
  const { mutateAsync } = usePostDownloadPdf()
  const rollbar = useRollbar()
  const { toast } = useToast()
  const { trackEvent: trackFSEvent } = useFSTrackEvent()
  const { trackDownload, trackActivity } = useActivityService()
  const [open, setOpen] = useState(defaultOpen)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const [projectSightModalOpen, setProjectSightModalOpen] = useState(false)

  const tocOption = crunchData.meta.hasToc && crunchData.meta.showToc
  const includeProvisions = crunchData.crunchType !== UDR_SOLUTION_TYPE_NAME

  let additionalOptions = [
    {
      label: 'Sticky notes, Highlights & Markups',
      fieldName: 'includeNotesMarkups',
      className: optionClassname,
      show: true,
      description: 'Manually created annotations in the document',
    },
  ]

  if (includeProvisions) {
    additionalOptions = [
      {
        label: keyProvisionText ? KEY_PROVISION_MATCHES : TABLE_OF_CONTENTS,
        fieldName: 'includeKeyProvisionMatches',
        className: optionClassname,
        show: tocOption,
        description: keyProvisionText
          ? 'List of all key provisions found powered by CrunchAI'
          : '',
      },
      {
        label: 'Key Provision Highlights',
        fieldName: 'includeProvisionHighlights',
        className: optionClassname,
        show: true,
        description: 'Auto-highlighted key provision text in the document',
      },
      ...additionalOptions,
    ]
  }

  const documentOption: DocumentOptions = {
    label: 'Document',
    fieldName: 'document' as const,
    className: '',
    show: true,
    id: 'document',
    additionalOptions,
    layoutOptions: [],
  }

  if (downloadConfigOptions) {
    documentOption.layoutOptions = [
      {
        label: COMPRESS_TEXT,
        fieldName: 'compress_text',
        className: optionClassname,
        show: true,
        description: '',
      },
      {
        label: LANDSCAPE,
        fieldName: 'landscape',
        className: optionClassname,
        show: true,
        description: '',
      },
    ]
  }

  const questionOptions = (index: number, c: ChecklistPreview) => {
    if (c.optionGroup === 1) {
      return [
        {
          label: OPTION_GROUPS[`o${c.optionGroup}`][1],
          fieldName: `checklists.${index}.include_option2`,
        },
        {
          label: OPTION_GROUPS[`o${c.optionGroup}`][2].includes('Review')
            ? 'Review'
            : OPTION_GROUPS[`o${c.optionGroup}`][2],
          fieldName: `checklists.${index}.include_option3`,
        },
        {
          label: OPTION_GROUPS[`o${c.optionGroup}`][0],
          fieldName: `checklists.${index}.include_option1`,
        },
        {
          label: 'No answer',
          fieldName: `checklists.${index}.include_no_answer`,
        },
      ]
    }
    return [
      {
        label: OPTION_GROUPS[`o${c.optionGroup}`][0],
        fieldName: `checklists.${index}.include_option1`,
      },
      {
        label: OPTION_GROUPS[`o${c.optionGroup}`][1],
        fieldName: `checklists.${index}.include_option2`,
      },
      {
        label: OPTION_GROUPS[`o${c.optionGroup}`][2].includes('Review')
          ? 'Review'
          : OPTION_GROUPS[`o${c.optionGroup}`][2],
        fieldName: `checklists.${index}.include_option3`,
      },
      {
        label: 'No answer',
        fieldName: `checklists.${index}.include_no_answer`,
      },
    ]
  }

  const checklistOptions =
    crunchData.appliedChecklists?.map((c, index) => ({
      label: `${c.name}`,
      fieldName: `checklists.${index}` as const,
      className: '',
      show: true,
      id: c.uuid,
      optionGroup: c.optionGroup as 1 | 2 | 3,
      questionOptions: questionOptions(index, c),
      additionalOptions: [
        {
          label: 'Descriptions',
          fieldName: `checklists.${index}.include_descriptions`,
          show: true,
        },
        {
          label: 'AI Summaries',
          fieldName: `checklists.${index}.include_gen_ai_summary`,
          show: c.hasGenAiResults,
        },
        {
          label: 'Subquestions',
          fieldName: `checklists.${index}.include_subquestions`,
          show: c.hasSubquestions,
        },
        {
          label: 'Notes',
          fieldName: `checklists.${index}.include_notes`,
          show: true,
        },
      ],
    })) || []

  const playbookOptions =
    crunchData.appliedPlaybooks?.map((p, index) => ({
      label: `${p.name}`,
      fieldName: `playbooks.${index}` as const,
      className: '',
      show: true,
      id: p.uuid,
      additionalOptions: [],
    })) || []

  const showProjectDetails = Boolean(
    crunchData.projectInfo?.id &&
      Object.keys(crunchData.projectInfo).some(
        (key) =>
          !['id', 'name', 'uuid'].includes(key) &&
          crunchData.projectInfo[key as keyof typeof crunchData.projectInfo]
      )
  )

  const projectOption = {
    label: 'Project',
    fieldName: 'includeProject',
    className: '',
    show: showProjectDetails,
    id: 'project',
    additionalOptions: [],
  }

  const options = [
    ...checklistOptions,
    ...playbookOptions,
    projectOption,
    documentOption,
  ].filter((o) => o.show)

  const [defaultValuesCache, setDefaultValuesCache] = useLocalStorage(
    `${crunchData.id}-values-default-download-pdf`,
    {},
    90
  )
  const [defaultChecklistsValuesCache, setDefaultChecklistsValuesCache] =
    useLocalStorage(`checklists-values-default-download-pdf`, {}, 90)

  const defaultChecklists = [
    ...(crunchData.appliedChecklists?.map((c) => {
      const template_uuid = c.checklist_template_uuid
      const includeChecklist = getDefaultValueAttrChecklist(
        template_uuid,
        'include',
        true
      )
      const checklist: ChecklistDataDownload = {
        uuid: c.uuid,
        template_uuid: template_uuid,
        include: getDefaultValueAttrChecklist(template_uuid, 'include'),
        include_option1:
          includeChecklist &&
          getDefaultValueAttrChecklist(template_uuid, 'include_option1'),
        include_option2:
          includeChecklist &&
          getDefaultValueAttrChecklist(template_uuid, 'include_option2'),
        include_option3:
          includeChecklist &&
          getDefaultValueAttrChecklist(template_uuid, 'include_option3'),
        include_gen_ai_summary:
          includeChecklist &&
          getDefaultValueAttrChecklist(template_uuid, 'include_gen_ai_summary'),
        include_notes:
          includeChecklist &&
          getDefaultValueAttrChecklist(template_uuid, 'include_notes'),
        include_subquestions:
          includeChecklist &&
          getDefaultValueAttrChecklist(template_uuid, 'include_subquestions'),
        include_descriptions:
          includeChecklist &&
          getDefaultValueAttrChecklist(template_uuid, 'include_descriptions'),
        include_no_answer:
          includeChecklist &&
          getDefaultValueAttrChecklist(template_uuid, 'include_no_answer'),
      }

      return checklist
    }) || []),
  ]

  const defaultValues = {
    crunchUuid: crunchData.id,
    document: defaultValuesCache?.document ?? true,
    includeProject:
      showProjectDetails && (defaultValuesCache?.includeProject ?? true),
    includeKeyProvisionMatches:
      defaultValuesCache?.includeKeyProvisionMatches ?? includeProvisions,
    includeNotesMarkups: defaultValuesCache?.includeNotesMarkups ?? true,
    includeProvisionHighlights:
      defaultValuesCache?.includeProvisionHighlights ?? includeProvisions,
    useOriginalDoc: defaultValuesCache?.useOriginalDoc ?? false,
    compress_text:
      (downloadConfigOptions && defaultValuesCache?.compress_text) ?? false,
    landscape:
      (downloadConfigOptions && defaultValuesCache?.landscape) ?? false,
    checklists: defaultChecklists,
    playbooks: [
      ...(crunchData.appliedPlaybooks?.map((p) => ({
        include:
          defaultValuesCache?.playbooks?.find(
            (d: DownloadDocumentPlaybooksettings) => d?.uuid === p.uuid
          )?.include ?? true,
        uuid: p.uuid,
      })) || []),
    ],
  }

  /**
   * Retrieves the default value for a checklist attribute.
   *
   * If the attribute is not found in the cache, it will return true.
   *
   * @param {string} templateUuid - The template UUID of the checklist.
   * @param {string} key - The key of the attribute to retrieve.
   *
   * @returns {boolean} - The default value of the attribute.
   */
  function getDefaultValueAttrChecklist(
    templateUuid: string,
    key: string,
    useChecklistCache = true
  ): boolean {
    // Define default value by cache's checklist
    if (
      useChecklistCache &&
      templateUuid in defaultChecklistsValuesCache &&
      key in defaultChecklistsValuesCache[templateUuid]
    ) {
      return defaultChecklistsValuesCache[templateUuid][key]
    }

    // Define default value by cache's crunch
    const defaultValueFromCrunch = defaultValuesCache?.checklists?.find(
      (d: ChecklistDataDownload) => d?.template_uuid === templateUuid
    )
    if (defaultValueFromCrunch !== undefined) {
      return defaultValueFromCrunch[key]
    }

    // No cache found
    return true
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const formValues = form.watch()

  useEffect(() => {
    setActiveSection(null)
  }, [open])

  useEffect(() => {
    const isDocumentChecked = formValues.document
    const isAnyChecklistChecked = formValues.checklists.some((c) => c.include)
    const isAnyPlaybookChecked = formValues.playbooks.some((p) => p.include)
    setIsDisabled(
      !(isDocumentChecked || isAnyChecklistChecked || isAnyPlaybookChecked)
    )
  }, [formValues])

  useEffect(() => {
    // update the form if crunchData changes
    form.reset(defaultValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crunchData])

  const trackFullStoryEvent = (bodyData: BodyDataDownload) => {
    const hasChecklists = bodyData.checklists.length > 0
    const hasPlaybooks = bodyData.playbooks.length > 0

    const event = {
      document_included: bodyData.document.include_document,
      key_provision_matches: bodyData.document.include_key_provision_matches,
      key_provision_highlights: bodyData.document.include_provision_highlights,
      notes_markups: bodyData.document.include_notes_markups,
      project_details: bodyData.document.include_project_details,
      original_document: bodyData.document.use_original_document,
      compress_text: bodyData.document?.compress_text ?? '',
      landscape: bodyData.document?.landscape ?? '',
      has_playbooks: hasPlaybooks,
      has_checklists: hasChecklists,
      playbook_included:
        hasPlaybooks ?? bodyData.playbooks.some((p) => p.include === true),
      checklist_included:
        hasChecklists ?? bodyData.checklists.some((c) => c.include === true),
    }

    trackFSEvent(FS.CRUNCH_DOWNLOAD, event)
  }

  const downloadCrunch = async () => {
    setIsDownloading(true)
    const values = form.getValues()
    const bodyData: BodyDataDownload = {
      document: {
        include_document: values.document,
        include_notes_markups: values.includeNotesMarkups,
        include_project_details: values.includeProject,
        include_provision_highlights: values.includeProvisionHighlights,
        include_key_provision_matches: values.includeKeyProvisionMatches,
        use_original_document: values.useOriginalDoc,
        compress_text: values?.compress_text ?? '',
        landscape: values?.landscape ?? '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      checklists: values.checklists.filter((checklist) => checklist.include),
      playbooks: values.playbooks.filter((playbook) => playbook.include),
    }

    trackFullStoryEvent(bodyData)

    try {
      const { error, data } = await mutateAsync({
        crunchUUID: crunchData.id,
        bodyData,
      })

      if (error) {
        throw error
      }

      const pdf = await updateLinkAnnotations(
        NEXT_PUBLIC_PSPDF_LICENCE_KEY,
        data
      )
      downloadPdf(
        new Blob([pdf], {
          type: 'application/pdf',
        }),
        crunchData.label
      )

      // Track activity service download
      trackDownload(crunchData.id, 'pdf', {
        crunchLabel: crunchData.label,
        fileName: `${crunchData.label}__DocumentCrunch_${new Date().toISOString().slice(0, 19).replace(/[-:T]/gm, '')}.pdf`,
        success: true,
      })

      toast({
        title: 'Your PDF has been downloaded!',
      })
      setOpen(false)
    } catch (error) {
      // Track failed download
      trackDownload(crunchData.id, 'pdf', {
        crunchLabel: crunchData.label,
        success: false,
        data: { error: String(error) },
      })

      toast({
        variant: 'error',
        title: 'An error occurred while downloading',
        description: 'If the issue persists, please contact support.',
      })
      rollbar.error('Error downloading crunch', error as any)
    }

    setIsDownloading(false)
  }

  const onChange = (fullFieldName: string) => {
    if (
      fullFieldName.includes('checklists') ||
      fullFieldName.includes('playbooks')
    ) {
      const fieldName = fullFieldName.split('.')[0] as
        | 'checklists'
        | 'playbooks'
      const fieldIndex = parseInt(fullFieldName.split('.')[1])
      const valuesOfChecklistBeforeUpdate: any = {}
      form.setValue(
        fieldName,
        form.getValues(fieldName).map((c, index) => {
          if (index !== fieldIndex) {
            return c
          }
          const template_uuid = (c as any).template_uuid
          Object.keys(c as ChecklistFormOptions).forEach((key) => {
            valuesOfChecklistBeforeUpdate[key] = (c as any)[key]

            const excludedKeys = new Set(['include', 'uuid', 'template_uuid'])

            if (!excludedKeys.has(key)) {
              if (!c.include) {
                ;(c as any)[key] =
                  defaultChecklistsValuesCache[template_uuid]?.[key] ?? true
              } else {
                ;(c as any)[key] = false
              }
            }
          })
          return { ...c, include: !c.include }
        })
      )
      // Update the include on checklist
      if (fieldName === 'checklists') {
        saveChecklistOnCache(
          form.getValues('checklists')[fieldIndex],
          valuesOfChecklistBeforeUpdate
        )
      }

      return
    }

    form.setValue(
      fullFieldName as keyof DownloadFormValues,
      !formValues[fullFieldName as keyof DownloadFormValues]
    )

    if (fullFieldName === 'document') {
      if (formValues[fullFieldName]) {
        form.setValue('includeKeyProvisionMatches', false)
        form.setValue('includeProvisionHighlights', false)
        form.setValue('includeNotesMarkups', false)
        form.setValue('compress_text', false)
        form.setValue('landscape', false)
      }
    } else {
      form.setValue('document', true)
    }

    setDefaultValuesCache(form.getValues())
  }

  const saveChecklistOnCache = (
    checklist: ChecklistDataDownload,
    checklistBefore?: ChecklistDataDownload
  ) => {
    if (!checklist?.template_uuid) {
      return
    }

    const checklistToSave = { ...defaultChecklistsValuesCache }
    if (checklistBefore) {
      checklistToSave[checklist.template_uuid] = checklistBefore
    }

    if (!checklist?.include) {
      checklistToSave[checklist.template_uuid].include = false
      setDefaultChecklistsValuesCache(checklistToSave)
      return
    }

    checklistToSave[checklist.template_uuid] = checklist

    setDefaultChecklistsValuesCache(checklistToSave)
  }

  const isChecked = (fullFieldName: string) => {
    if (
      fullFieldName.includes('checklists') ||
      fullFieldName.includes('playbooks')
    ) {
      const fieldName = fullFieldName.split('.')[0] as
        | 'checklists'
        | 'playbooks'
      const fieldIndex = parseInt(fullFieldName.split('.')[1])
      return formValues[fieldName][fieldIndex]?.include
    }
    return Boolean(formValues[fullFieldName as keyof DownloadFormValues])
  }

  const handleExportToPDF = (
    e:
      | React.MouseEvent<HTMLDivElement, MouseEvent>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault()
    // Track download button click
    trackActivity(ActivityCategory.CRUNCH, ActivitySubCategory.OPEN_DOWNLOAD, {
      itemGuid: crunchData.id,
      itemType: ActivityItemType.CRUNCH,
      success: true,
      data: {
        action: 'download_button_clicked',
        crunch_label: crunchData.label,
        user_action: 'open_download_dialog',
      },
    })
    setOpen(true)
  }

  const handleExportToProjectSight = () => {
    trackActivity(ActivityCategory.CRUNCH, ActivitySubCategory.OPEN_DOWNLOAD, {
      itemGuid: crunchData?.id || '',
      itemType: ActivityItemType.CRUNCH,
      success: true,
      data: {
        action: 'projectsight_export_clicked',
        crunch_label: crunchData?.label || '',
        user_action: 'open_projectsight_export_dialog',
      },
    })

    setProjectSightModalOpen(true)
  }

  const getTooltipMessage = (
    hasPspdfkitInstance: boolean,
    messageType: 'export' | 'download'
  ) => {
    const capitalized = capitalize(messageType)
    if (isVersionCompV2Selected) {
      return versionCompareDiscoverabilityImprovements
        ? `Switch to V1 via the tabs to the left of the document.`
        : `${capitalized} are not available in this version during beta. Switch to V1 via the tabs to the left of the document.`
    }

    return hasPspdfkitInstance ? capitalized : undefined
  }

  return (
    <>
      {projectSightIntegration ? (
        // Export dropdown with ProjectSight option when projectSightIntegration is ON
        <DropdownMenu>
          <Tooltip
            content={getTooltipMessage(!!pspdfkitInstance, 'export')}
            withArrow
            className={cn(
              'tw-max-w-[200px] tw-whitespace-normal',
              !isVersionCompV2Selected && 'md:tw-hidden'
            )}
            active={!!pspdfkitInstance}
          >
            {/* wrapping div is required so that a tooltip can still be displayed even when the item is disabled */}
            <div className='tw-inline-block'>
              <DropdownMenuTrigger
                asChild
                disabled={!pspdfkitInstance || isVersionCompV2Selected}
              >
                <Button
                  data-testid='export-dropdown-trigger'
                  size='sm'
                  variant='outline'
                >
                  <Icon
                    icon={faDownload}
                    color={IconColor.darkPrimary}
                    size='1x'
                    className='lg:tw-mr-2'
                  />
                  <span className='tw-hidden lg:tw-block tw-text-sm tw-font-medium'>
                    Export
                  </span>
                </Button>
              </DropdownMenuTrigger>
            </div>
          </Tooltip>
          <DropdownMenuContent data-testid='export-dialog-trigger'>
            <DropdownMenuItem
              data-testid='export-to-pdf'
              onClick={handleExportToPDF}
            >
              Export to PDF
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid='export-to-projectsight'
              onClick={handleExportToProjectSight}
              disabled={
                checklistOptions.length === 0 && playbookOptions.length === 0
              }
            >
              Export to ProjectSight
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        // Simple download button when projectSightIntegration is OFF
        <Tooltip
          content={getTooltipMessage(!!pspdfkitInstance, 'download')}
          withArrow
          className={cn(
            'tw-max-w-[200px] tw-whitespace-normal',
            !isVersionCompV2Selected && 'md:tw-hidden'
          )}
          active={!!pspdfkitInstance}
        >
          {/* wrapping div is required so that a tooltip can still be displayed even when the item is disabled - specifically for version comparison */}
          <div className='tw-inline-block'>
            <Button
              data-testid='download-button-trigger'
              disabled={!pspdfkitInstance || isVersionCompV2Selected}
              size='sm'
              variant='outline'
              onClick={handleExportToPDF}
            >
              <Icon
                icon={faDownload}
                color={IconColor.darkPrimary}
                size='1x'
                className='lg:tw-mr-2'
              />
              <span className='tw-hidden lg:tw-block tw-text-sm tw-font-medium'>
                Download
              </span>
            </Button>
          </div>
        </Tooltip>
      )}

      <BaseDialog
        title='What do you want to include in your PDF?'
        description='Everything will be included unless the selection is modified.'
        isOpen={open}
        onClose={() => setOpen(false)}
        size='large'
        data-testid='download-dialog-content'
        primaryActionSlot={
          <Button
            data-testid='download-button'
            disabled={isDisabled || isDownloading}
            onClick={downloadCrunch}
          >
            {isDownloading ? 'Downloading...' : 'Download'}
          </Button>
        }
      >
        {isDownloading && (
          <div className='tw-absolute tw-inset-0 tw-bg-neutral-100 tw-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-10'>
            <Spinner className='tw-h-12 tw-w-12' />
          </div>
        )}

        <div>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(() => {})}
              className='tw-space-y-4'
            >
              <div className='tw-flex tw-justify-between tw-overflow-y-auto tw-bg-subtle tw-py-1 tw-px-2'>
                <div className='tw-flex tw-flex-col tw-gap-6 tw-w-full'>
                  <div className='tw-flex tw-gap-2 tw-overflow-hidden tw-w-full'>
                    <div
                      className={cn(
                        'tw-w-full tw-overflow-auto',
                        activeSection && 'tw-w-1/2'
                      )}
                    >
                      <div className='tw-uppercase tw-text-neutral-500 tw-text-sm tw-p-4 tw-pb-5'>
                        Select documents
                      </div>
                      {[...options].map((o, index) => (
                        <div
                          key={o.fieldName}
                          className={cn(
                            'tw-flex tw-flex-col tw-gap-6 tw-pb-6 tw-pt-6 tw-w-full tw-px-4 -tw-mt-1',
                            index !== options.length - 1 &&
                              'tw-border-b tw-border-neutral-200',
                            activeSection === o.fieldName
                              ? 'tw-rounded tw-border tw-border-neutral-200 tw-bg-white tw-pt-6'
                              : ''
                          )}
                        >
                          <FormField
                            key={o.fieldName}
                            control={form.control}
                            name={o.fieldName as keyof DownloadFormValues}
                            render={() => (
                              <FormItem
                                className={cn(
                                  'tw-flex tw-items-center tw-gap-4',
                                  o.className
                                )}
                              >
                                <FormControl className='tw-flex'>
                                  <Checkbox
                                    id={`${o.fieldName}-checkbox`}
                                    checked={isChecked(o.fieldName)}
                                    onClick={() => {
                                      onChange(o.fieldName)
                                    }}
                                    data-testid={`checkbox-${o.fieldName}`}
                                  />
                                </FormControl>
                                <div className='tw-flex tw-w-full !tw-mt-0 tw-items-center tw-justify-between tw-gap-2'>
                                  <FormLabel
                                    onClick={() => onChange(o.fieldName)}
                                    className='!tw-my-0 tw-font-normal tw-text-base'
                                  >
                                    {o.label}
                                  </FormLabel>
                                  {!o.fieldName.includes('playbooks') &&
                                    !o.fieldName.includes('includeProject') && (
                                      <Button
                                        variant={'ghost'}
                                        className='tw-text-primary tw-flex tw-gap-2 tw-text-sm !tw-p-0 hover:tw-bg-transparent hover:tw-text-primary tw-h-auto'
                                        onClick={() => {
                                          if (activeSection === o.fieldName) {
                                            setActiveSection(null)
                                          } else {
                                            setActiveSection(o.fieldName)
                                          }
                                        }}
                                        data-testid={`modify-${o.fieldName}`}
                                      >
                                        Modify{' '}
                                        <Icon
                                          className='tw-text-primary'
                                          icon={faChevronRight}
                                        />
                                      </Button>
                                    )}
                                </div>
                              </FormItem>
                            )}
                          />
                        </div>
                      ))}
                    </div>
                    {activeSection && (
                      <DownloadDialogExpanded
                        activeSection={activeSection}
                        form={form}
                        checklistOptions={checklistOptions}
                        documentOption={documentOption}
                        setDefaultValuesCache={setDefaultValuesCache}
                        saveChecklistOnCache={saveChecklistOnCache}
                        onChange={onChange}
                      />
                    )}
                  </div>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </BaseDialog>

      {/* ProjectSight Export Modal with Authentication */}
      <ProjectSightModalWrapper
        open={projectSightModalOpen}
        onOpenChange={setProjectSightModalOpen}
        checklistOptions={checklistOptions}
        playbookOptions={playbookOptions}
        documentName={crunchData.label}
        crunchUUID={crunchData.id}
      />
    </>
  )
}
